import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { apiUrl, fetchJson } from '../../apiBase';
import { getEvaluationResultDetail, listEvaluationResults, listThemes } from '../api/values';

const FREE_TEXT_SECTIONS = [
  { key: 'notes', fallbackLabel: 'Ek notlar' },
  { key: 'strengths', fallbackLabel: 'Güçlü yönleri' },
  { key: 'improvements', fallbackLabel: 'Gelişim alanları' },
  { key: 'general_comment', fallbackLabel: 'Genel yorum' },
];

const buildFreeTextSections = (detail) => {
  if (!detail) return [];
  const provided = Array.isArray(detail.free_text_sections) ? detail.free_text_sections : null;
  if (provided && provided.length > 0) {
    return provided.map((section) => ({
      key: section.key || section.label,
      label: section.label || section.key || 'Not',
      value: section.value ?? '',
    }));
  }
  const fallbackLabels = {
    notes: detail?.form?.notes_label || FREE_TEXT_SECTIONS[0].fallbackLabel,
    strengths: detail?.form?.strengths_label || FREE_TEXT_SECTIONS[1].fallbackLabel,
    improvements: detail?.form?.improvements_label || FREE_TEXT_SECTIONS[2].fallbackLabel,
    general_comment: detail?.form?.general_label || FREE_TEXT_SECTIONS[3].fallbackLabel,
  };
  return FREE_TEXT_SECTIONS.map(({ key, fallbackLabel }) => ({
    key,
    label: fallbackLabels[key] || fallbackLabel,
    value: detail?.[key] ?? '',
  }));
};

const getStudentName = (row, detail) => detail?.student_display_name || row.student_display_name || row.student_id || '-';
const getParentName = (row, detail) => detail?.parent_display_name || row.parent_display_name || '';

const ValuesEvaluationResults = ({ schoolId, onError }) => {
  const [themeId, setThemeId] = useState('');
  const [classId, setClassId] = useState('');
  const [studentId, setStudentId] = useState('');
  const [classOptions, setClassOptions] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [studentOptions, setStudentOptions] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [themes, setThemes] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [detailCache, setDetailCache] = useState({});
  const [detailLoading, setDetailLoading] = useState({});
  const [detailError, setDetailError] = useState({});

  useEffect(() => {
    let cancelled = false;
    const loadClasses = async () => {
      if (!schoolId) {
        setClassOptions([]);
        setClassId('');
        return;
      }
      setLoadingClasses(true);
      try {
        const res = await fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}`));
        if (!res.ok) throw new Error(await res.text());
        const rows = await res.json();
        const options = (rows || [])
          .map((item) => {
            if (!item) return null;
            if (typeof item === 'string') return null;
            const rawId = item?.id ?? item?.class_id;
            const id = Number(rawId);
            const name = item?.name ?? item?.code;
            if (!Number.isFinite(id) || !name) return null;
            return { id: String(id), name: String(name) };
          })
          .filter(Boolean)
          .sort((a, b) => a.name.localeCompare(b.name, 'tr-TR'));
        if (!cancelled) {
          setClassOptions(options);
          setClassId((prev) => {
            if (prev && options.some((opt) => opt.id === prev)) return prev;
            return '';
          });
        }
      } catch (_) {
        if (!cancelled) {
          setClassOptions([]);
          setClassId('');
          onError?.('Şubeler yüklenemedi.');
        }
      } finally {
        if (!cancelled) setLoadingClasses(false);
      }
    };

    loadClasses();
    return () => {
      cancelled = true;
    };
  }, [schoolId, onError]);

  useEffect(() => {
    let cancelled = false;
    const loadStudents = async () => {
      if (!schoolId || !classId) {
        setStudentOptions([]);
        setStudentId('');
        return;
      }
      setLoadingStudents(true);
      try {
        const res = await fetchJson(
          apiUrl(`/admin/students/${encodeURIComponent(schoolId)}/by-class/${encodeURIComponent(classId)}`),
        );
        if (!res.ok) throw new Error(await res.text());
        const rows = await res.json();
        const options = (rows || [])
          .map((student) => {
            if (!student) return null;
            const rawId = student?.id ?? student?.student_id;
            const id = Number(rawId);
            if (!Number.isFinite(id)) return null;
            const name = String(student?.name || student?.first_name || '').trim();
            const surname = String(student?.surname || student?.last_name || '').trim();
            const studentNo = student?.student_no ?? student?.no;
            const displayName = `${name} ${surname}`.trim();
            const label = `${studentNo ? `${studentNo} - ` : ''}${displayName || id}`;
            return { id: String(id), label, displayName };
          })
          .filter(Boolean)
          .sort((a, b) => (
            a.displayName.localeCompare(b.displayName, 'tr-TR', { sensitivity: 'base' }) ||
            Number(a.id) - Number(b.id)
          ));

        if (!cancelled) {
          setStudentOptions(options);
          setStudentId((prev) => {
            if (prev && options.some((opt) => opt.id === prev)) return prev;
            return '';
          });
        }
      } catch (_) {
        if (!cancelled) {
          setStudentOptions([]);
          setStudentId('');
          onError?.('Öğrenciler yüklenemedi.');
        }
      } finally {
        if (!cancelled) setLoadingStudents(false);
      }
    };

    loadStudents();
    return () => {
      cancelled = true;
    };
  }, [schoolId, classId, onError]);

  const loadData = async () => {
    setExpanded({});
    setDetailCache({});
    setDetailLoading({});
    setDetailError({});
    setLoading(true);
    try {
      const [resList, resThemes] = await Promise.all([
        listEvaluationResults(schoolId, {
          theme_id: themeId || undefined,
          class_id: classId || undefined,
          student_id: studentId || undefined,
        }),
        listThemes(schoolId),
      ]);
      setResults(resList.items || []);
      setThemes((resThemes.items || []).map((t) => ({ value: t.id, label: t.title || `Tema ${t.id}` })));
    } catch (e) {
      onError?.('Sonuçlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, themeId, classId, studentId]);

  const grouped = useMemo(() => {
    const byTheme = {};
    results.forEach((r) => {
      const key = r.theme_title || (r.theme_id ? `Tema ${r.theme_id}` : 'tema');
      if (!byTheme[key]) byTheme[key] = [];
      byTheme[key].push(r);
    });
    return byTheme;
  }, [results]);

  const fetchDetail = useCallback(
    async (evaluationId) => {
      if (!evaluationId) return;
      setDetailLoading((prev) => ({ ...prev, [evaluationId]: true }));
      setDetailError((prev) => ({ ...prev, [evaluationId]: null }));
      try {
        const res = await getEvaluationResultDetail(schoolId, evaluationId);
        setDetailCache((prev) => ({ ...prev, [evaluationId]: res.item || null }));
      } catch (err) {
        setDetailError((prev) => ({ ...prev, [evaluationId]: 'Detaylar yüklenemedi.' }));
      } finally {
        setDetailLoading((prev) => ({ ...prev, [evaluationId]: false }));
      }
    },
    [schoolId]
  );

  const handleToggle = (evaluationId, forceState) => {
    if (!evaluationId) return;
    const willOpen = typeof forceState === 'boolean' ? forceState : !expanded[evaluationId];
    setExpanded((prev) => ({ ...prev, [evaluationId]: willOpen }));
    if (willOpen && !detailCache[evaluationId] && !detailLoading[evaluationId]) {
      fetchDetail(evaluationId);
    }
  };

  const formatSubmittedAt = (value) => {
    if (!value) return '-';
    try {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
      }
    } catch (err) {
      // ignore parsing errors
    }
    return String(value).replace('T', ' ');
  };

  const formatAnswerLabel = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const normalized = String(value).trim().toLowerCase();
    if (normalized === 'yes') return 'Evet';
    if (normalized === 'no') return 'Hayır';
    return String(value);
  };

  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SummarizeIcon fontSize="small" /> Değerlendirme Sonuçları
        </Typography>
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={loadData} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            select
            label="Tema"
            value={themeId}
            onChange={(e) => setThemeId(e.target.value)}
            fullWidth
          >
            <MenuItem value="">Tümü</MenuItem>
            {themes.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            select
            label="Şube"
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value);
              setStudentId('');
            }}
            fullWidth
            disabled={loadingClasses}
          >
            <MenuItem value="">Tümü</MenuItem>
            {classOptions.map((cls) => (
              <MenuItem key={cls.id} value={cls.id}>
                {cls.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            select
            label="Öğrenci"
            value={studentId}
            onChange={(e) => setStudentId(e.target.value)}
            fullWidth
            disabled={loadingStudents || !classId}
          >
            <MenuItem value="">Tümü</MenuItem>
            {studentOptions.map((student) => (
              <MenuItem key={student.id} value={student.id}>
                {student.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Yükleniyor...
          </Typography>
        </Stack>
      )}

      {Object.keys(grouped).length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary">
          Henüz sonuç yok.
        </Typography>
      )}

      {Object.entries(grouped).map(([code, items]) => (
        <Box key={code} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {code}
            </Typography>
            <Chip size="small" label={`${items.length} kayıt`} />
          </Stack>
          <Stack spacing={1.5}>
            {items.map((r) => {
              const detail = detailCache[r.id];
              const isExpanded = !!expanded[r.id];
              const isLoadingDetail = !!detailLoading[r.id];
              const error = detailError[r.id];
              const textSections = detail ? buildFreeTextSections(detail) : [];
              const hasAnswerDetails = detail ? (detail.answer_details || []).length > 0 : false;
              const studentDisplay = getStudentName(r, detail);
              const parentDisplay = getParentName(r, detail);
              const submitted = formatSubmittedAt(detail?.submitted_at || r.submitted_at);
              const totalCount = Number(r.total_count || 0);
              const yesCount = Number(r.yes_count || 0);
              const noCount = Number.isFinite(r.no_count) ? Number(r.no_count) : Math.max(0, totalCount - yesCount);

              return (
                <Accordion
                  key={r.id || `${code}-${r.student_id}`}
                  expanded={isExpanded}
                  onChange={(_, next) => handleToggle(r.id, next)}
                  disableGutters
                  square
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6} md={5}>
                        <Typography
                          variant="body2"
                          title={`${studentDisplay} - Veli: ${parentDisplay || '—'}`}
                          sx={{ lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          <Box component="span" sx={{ fontWeight: 700 }}>
                            {studentDisplay}
                          </Box>
                          <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                            {' - '}Veli: {parentDisplay || '—'}
                          </Box>
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Gönderim
                        </Typography>
                        <Typography variant="body2">{submitted}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3} md={2}>
                        <Typography variant="caption" color="text.secondary">
                          Yanıtlar
                        </Typography>
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                          <Chip size="small" color="success" variant="outlined" label={`Evet: ${yesCount}`} />
                          <Chip size="small" color="error" variant="outlined" label={`Hayır: ${noCount}`} />
                        </Stack>
                      </Grid>
                    </Grid>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 1.5, pb: 2 }}>
                    {isLoadingDetail && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Detaylar yükleniyor...
                        </Typography>
                      </Stack>
                    )}
                    {!isLoadingDetail && error && (
                      <Typography variant="body2" color="error.main">
                        {error}
                      </Typography>
                    )}
                    {!isLoadingDetail && !error && detail && (
                      <Stack spacing={2}>
                        {hasAnswerDetails && (
                          <Stack spacing={1.5}>
                            {detail.answer_details.map((group) => (
                              <Paper
                                key={`${r.id}-${group.group_id || group.group_title}`}
                                variant="outlined"
                                sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  justifyContent="space-between"
                                  sx={{ mb: 1 }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    {group.emoji ? `${group.emoji} ` : ''}
                                    {group.group_title || 'Yanıtlar'}
                                  </Typography>
                                </Stack>
                                <Stack spacing={1}>
                                  {group.questions.map((q) => (
                                    <Stack
                                      key={q.id}
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      sx={{ bgcolor: 'grey.50', borderRadius: 1.5, px: 1.5, py: 1, flexWrap: 'wrap' }}
                                    >
                                      <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0, fontWeight: 500 }}>
                                        {q.text || q.id}
                                      </Typography>
                                      <Chip
                                        size="small"
                                        variant={q.is_yes === true || q.is_yes === false ? 'filled' : 'outlined'}
                                        color={q.is_yes === true ? 'success' : q.is_yes === false ? 'error' : 'default'}
                                        label={formatAnswerLabel(q.answer)}
                                      />
                                    </Stack>
                                  ))}
                                </Stack>
                              </Paper>
                            ))}
                          </Stack>
                        )}

                        <Stack spacing={1.5}>
                          {textSections.map((section) => {
                            const value = String(section.value || '').trim();
                            return (
                              <Paper
                                key={`${r.id}-${section.key}`}
                                variant="outlined"
                                sx={{ borderRadius: 2, p: { xs: 1.5, sm: 2 } }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  {section.label}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                                  {value || '—'}
                                </Typography>
                              </Paper>
                            );
                          })}
                        </Stack>

                        {!hasAnswerDetails && (
                          <Typography variant="body2" color="text.secondary">
                            Henüz detay verisi yok.
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Paper>
  );
};

export default ValuesEvaluationResults;
